import { apiRoute } from "./config";

export type MarkAttentionCategory =
  | "blocking"
  | "action_required"
  | "input_requested"
  | "attention";
export type MarkAttentionUrgency = "critical" | "high" | "normal";
export type MarkAttentionAcknowledgement = "read" | "ok";
export type MarkAttentionState = "open" | "read" | "ok" | "resolved";

export interface MarkAttentionEvidence {
  label: string;
  url: string;
}

export interface MarkAttentionComment {
  id: string;
  itemRevision: number;
  author: {
    kind: "mark" | "agent";
    label: string;
  };
  body: string;
  createdAt: string;
}

export interface MarkAttentionItem {
  id: string;
  revision: number;
  category: MarkAttentionCategory;
  state: MarkAttentionState;
  urgency: MarkAttentionUrgency;
  title: string;
  summary: string;
  requiredAction: string;
  responseInstructions: string;
  owner: {
    label: string;
    email: string;
  };
  sourceTaskID: string | null;
  evidence: MarkAttentionEvidence[];
  createdAt: string;
  lastActivityAt: string;
  updatedAt: string;
  earlierCommentCount: number;
  comments: MarkAttentionComment[];
}

export interface MarkAttentionRepository {
  load(): Promise<MarkAttentionItem[]>;
  loadThreads(): Promise<MarkAttentionItem[]>;
  acknowledge(
    itemID: string,
    revision: number,
    state: MarkAttentionAcknowledgement,
  ): Promise<void>;
  comment(
    itemID: string,
    revision: number,
    body: string,
    clientRequestID: string,
  ): Promise<{
    revision: number;
    state: "open";
    comment: MarkAttentionComment;
  }>;
}

export class MarkAttentionRepositoryError extends Error {
  constructor(
    readonly status: number | null,
    message = "mark_attention_unavailable",
  ) {
    super(message);
  }
}

const categories = new Set<MarkAttentionCategory>([
  "blocking",
  "action_required",
  "input_requested",
  "attention",
]);
const urgencies = new Set<MarkAttentionUrgency>([
  "critical",
  "high",
  "normal",
]);

function isObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && !Array.isArray(value) && typeof value === "object";
}

function hasExactKeys(
  value: Record<string, unknown>,
  keys: readonly string[],
): boolean {
  return Object.keys(value).sort().join("\u0000")
    === [...keys].sort().join("\u0000");
}

function boundedString(
  value: unknown,
  maximum: number,
): value is string {
  return typeof value === "string"
    && value.length > 0
    && value.length <= maximum;
}

function evidence(value: unknown): MarkAttentionEvidence[] {
  if (!Array.isArray(value) || value.length > 12) {
    throw new Error("invalid_mark_attention_payload");
  }
  return value.map((candidate) => {
    if (
      !isObject(candidate)
      || !hasExactKeys(candidate, ["label", "url"])
      || !boundedString(candidate.label, 120)
      || !boundedString(candidate.url, 1000)
    ) {
      throw new Error("invalid_mark_attention_payload");
    }
    let url: URL;
    try {
      url = new URL(candidate.url);
    } catch {
      throw new Error("invalid_mark_attention_payload");
    }
    if (url.protocol !== "https:") {
      throw new Error("invalid_mark_attention_payload");
    }
    return { label: candidate.label, url: url.toString() };
  });
}

function item(value: unknown): MarkAttentionItem {
  if (
    !isObject(value)
    || !hasExactKeys(value, [
      "id",
      "revision",
      "category",
      "state",
      "urgency",
      "title",
      "summary",
      "required_action",
      "response_instructions",
      "owner",
      "source_task_id",
      "evidence",
      "last_activity_at",
    ])
    || !boundedString(value.id, 160)
    || typeof value.revision !== "number"
    || !Number.isSafeInteger(value.revision)
    || value.revision < 1
    || typeof value.category !== "string"
    || !categories.has(value.category as MarkAttentionCategory)
    || value.state !== "open"
    || typeof value.urgency !== "string"
    || !urgencies.has(value.urgency as MarkAttentionUrgency)
    || !boundedString(value.title, 180)
    || !boundedString(value.summary, 1200)
    || !boundedString(value.required_action, 1200)
    || !boundedString(value.response_instructions, 1200)
    || (
      value.source_task_id !== null
      && !boundedString(value.source_task_id, 160)
    )
    || !boundedString(value.last_activity_at, 100)
    || !Number.isFinite(Date.parse(value.last_activity_at))
    || !isObject(value.owner)
    || !hasExactKeys(value.owner, ["label", "email"])
    || !boundedString(value.owner.label, 100)
    || !boundedString(value.owner.email, 254)
  ) {
    throw new Error("invalid_mark_attention_payload");
  }
  return {
    id: value.id,
    revision: value.revision,
    category: value.category as MarkAttentionCategory,
    state: "open",
    urgency: value.urgency as MarkAttentionUrgency,
    title: value.title,
    summary: value.summary,
    requiredAction: value.required_action,
    responseInstructions: value.response_instructions,
    owner: {
      label: value.owner.label,
      email: value.owner.email,
    },
    sourceTaskID: value.source_task_id,
    evidence: evidence(value.evidence),
    createdAt: value.last_activity_at,
    lastActivityAt: value.last_activity_at,
    updatedAt: value.last_activity_at,
    earlierCommentCount: 0,
    comments: [],
  };
}

function threadComment(value: unknown): MarkAttentionComment {
  if (
    !isObject(value)
    || !hasExactKeys(value, [
      "id",
      "item_revision",
      "author",
      "body",
      "created_at",
    ])
    || !boundedString(value.id, 220)
    || typeof value.item_revision !== "number"
    || !Number.isSafeInteger(value.item_revision)
    || value.item_revision < 1
    || !isObject(value.author)
    || !hasExactKeys(value.author, ["kind", "label"])
    || (value.author.kind !== "mark" && value.author.kind !== "agent")
    || !boundedString(value.author.label, 100)
    || !boundedString(value.body, 4000)
    || !boundedString(value.created_at, 100)
    || !Number.isFinite(Date.parse(value.created_at))
  ) {
    throw new Error("invalid_mark_attention_payload");
  }
  return {
    id: value.id,
    itemRevision: value.item_revision,
    author: {
      kind: value.author.kind,
      label: value.author.label,
    },
    body: value.body,
    createdAt: value.created_at,
  };
}

function threadItem(value: unknown): MarkAttentionItem {
  if (
    !isObject(value)
    || !hasExactKeys(value, [
      "id",
      "revision",
      "category",
      "state",
      "urgency",
      "title",
      "summary",
      "required_action",
      "response_instructions",
      "owner",
      "source_task_id",
      "evidence",
      "created_at",
      "last_activity_at",
      "updated_at",
      "earlier_comment_count",
      "comments",
    ])
    || !boundedString(value.id, 160)
    || typeof value.revision !== "number"
    || !Number.isSafeInteger(value.revision)
    || value.revision < 1
    || typeof value.category !== "string"
    || !categories.has(value.category as MarkAttentionCategory)
    || typeof value.state !== "string"
    || !["open", "read", "ok", "resolved"].includes(value.state)
    || typeof value.urgency !== "string"
    || !urgencies.has(value.urgency as MarkAttentionUrgency)
    || !boundedString(value.title, 180)
    || !boundedString(value.summary, 1200)
    || !boundedString(value.required_action, 1200)
    || !boundedString(value.response_instructions, 1200)
    || (
      value.source_task_id !== null
      && !boundedString(value.source_task_id, 160)
    )
    || !boundedString(value.created_at, 100)
    || !Number.isFinite(Date.parse(value.created_at))
    || !boundedString(value.last_activity_at, 100)
    || !Number.isFinite(Date.parse(value.last_activity_at))
    || !boundedString(value.updated_at, 100)
    || !Number.isFinite(Date.parse(value.updated_at))
    || typeof value.earlier_comment_count !== "number"
    || !Number.isSafeInteger(value.earlier_comment_count)
    || value.earlier_comment_count < 0
    || !isObject(value.owner)
    || !hasExactKeys(value.owner, ["label", "email"])
    || !boundedString(value.owner.label, 100)
    || !boundedString(value.owner.email, 254)
    || !Array.isArray(value.comments)
    || value.comments.length > 500
  ) {
    throw new Error("invalid_mark_attention_payload");
  }
  const comments = value.comments.map(threadComment);
  const commentIDs = new Set<string>();
  for (const comment of comments) {
    if (commentIDs.has(comment.id)) {
      throw new Error("invalid_mark_attention_payload");
    }
    commentIDs.add(comment.id);
  }
  return {
    id: value.id,
    revision: value.revision,
    category: value.category as MarkAttentionCategory,
    state: value.state as MarkAttentionState,
    urgency: value.urgency as MarkAttentionUrgency,
    title: value.title,
    summary: value.summary,
    requiredAction: value.required_action,
    responseInstructions: value.response_instructions,
    owner: {
      label: value.owner.label,
      email: value.owner.email,
    },
    sourceTaskID: value.source_task_id,
    evidence: evidence(value.evidence),
    createdAt: value.created_at,
    lastActivityAt: value.last_activity_at,
    updatedAt: value.updated_at,
    earlierCommentCount: value.earlier_comment_count,
    comments,
  };
}

export function parseMarkAttentionThreadsPayload(
  value: unknown,
): MarkAttentionItem[] {
  if (
    !isObject(value)
    || !hasExactKeys(value, ["schema_version", "items"])
    || value.schema_version !== "mark-attention-threads-v1"
    || !Array.isArray(value.items)
    || value.items.length > 100
  ) {
    throw new Error("invalid_mark_attention_payload");
  }
  const items = value.items.map(threadItem);
  const ids = new Set<string>();
  for (const candidate of items) {
    if (ids.has(candidate.id)) {
      throw new Error("invalid_mark_attention_payload");
    }
    ids.add(candidate.id);
  }
  return items;
}

export function parseMarkAttentionPayload(value: unknown): MarkAttentionItem[] {
  if (
    !isObject(value)
    || !hasExactKeys(value, ["schema_version", "items"])
    || value.schema_version !== "mark-attention-v1"
    || !Array.isArray(value.items)
    || value.items.length > 100
  ) {
    throw new Error("invalid_mark_attention_payload");
  }
  const ids = new Set<string>();
  const items = value.items.map(item);
  for (const candidate of items) {
    if (ids.has(candidate.id)) {
      throw new Error("invalid_mark_attention_payload");
    }
    ids.add(candidate.id);
  }
  return items;
}

async function jsonValue(response: Response): Promise<unknown> {
  const contentType = response.headers.get("content-type")?.toLowerCase() ?? "";
  if (!contentType.includes("application/json")) {
    throw new Error("unexpected_response");
  }
  return response.json();
}

export class CloudflareMarkAttentionRepository
  implements MarkAttentionRepository {
  async load(): Promise<MarkAttentionItem[]> {
    let response: Response;
    try {
      response = await fetch(apiRoute("/mark-attention"), {
        credentials: "same-origin",
        cache: "no-store",
      });
    } catch {
      throw new MarkAttentionRepositoryError(null);
    }
    if (!response.ok) {
      throw new MarkAttentionRepositoryError(response.status);
    }
    try {
      return parseMarkAttentionPayload(await jsonValue(response));
    } catch {
      throw new MarkAttentionRepositoryError(response.status);
    }
  }

  async loadThreads(): Promise<MarkAttentionItem[]> {
    let response: Response;
    try {
      response = await fetch(apiRoute("/mark-attention/threads"), {
        credentials: "same-origin",
        cache: "no-store",
      });
    } catch {
      throw new MarkAttentionRepositoryError(null);
    }
    if (!response.ok) {
      throw new MarkAttentionRepositoryError(response.status);
    }
    try {
      return parseMarkAttentionThreadsPayload(await jsonValue(response));
    } catch {
      throw new MarkAttentionRepositoryError(response.status);
    }
  }

  async comment(
    itemID: string,
    revision: number,
    body: string,
    clientRequestID: string,
  ): Promise<{
    revision: number;
    state: "open";
    comment: MarkAttentionComment;
  }> {
    let response: Response;
    try {
      response = await fetch(
        apiRoute(
          `/mark-attention/items/${encodeURIComponent(itemID)}/comments`,
        ),
        {
          method: "POST",
          credentials: "same-origin",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            body,
            client_request_id: clientRequestID,
            item_revision: revision,
          }),
        },
      );
    } catch {
      throw new MarkAttentionRepositoryError(null);
    }
    if (!response.ok) {
      throw new MarkAttentionRepositoryError(response.status);
    }
    try {
      const payload = await jsonValue(response);
      if (
        !isObject(payload)
        || !hasExactKeys(payload, ["schema_version", "item", "comment"])
        || payload.schema_version !== "mark-attention-comment-v1"
        || !isObject(payload.item)
        || !hasExactKeys(payload.item, ["id", "revision", "state"])
        || payload.item.id !== itemID
        || typeof payload.item.revision !== "number"
        || !Number.isSafeInteger(payload.item.revision)
        || payload.item.revision < revision
        || payload.item.state !== "open"
      ) {
        throw new Error("unexpected_response");
      }
      return {
        revision: payload.item.revision,
        state: "open",
        comment: threadComment(payload.comment),
      };
    } catch {
      throw new MarkAttentionRepositoryError(response.status);
    }
  }

  async acknowledge(
    itemID: string,
    revision: number,
    state: MarkAttentionAcknowledgement,
  ): Promise<void> {
    let response: Response;
    try {
      response = await fetch(
        apiRoute(`/mark-attention/items/${encodeURIComponent(itemID)}`),
        {
          method: "PATCH",
          credentials: "same-origin",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ state, revision }),
        },
      );
    } catch {
      throw new MarkAttentionRepositoryError(null);
    }
    if (!response.ok) {
      throw new MarkAttentionRepositoryError(response.status);
    }
    try {
      const payload = await jsonValue(response);
      if (
        !isObject(payload)
        || !hasExactKeys(payload, ["schema_version", "item"])
        || payload.schema_version !== "mark-attention-ack-v1"
        || !isObject(payload.item)
        || !hasExactKeys(payload.item, ["id", "revision", "state"])
        || payload.item.id !== itemID
        || payload.item.revision !== revision
        || payload.item.state !== state
      ) {
        throw new Error("unexpected_response");
      }
    } catch {
      throw new MarkAttentionRepositoryError(response.status);
    }
  }
}
